import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import {
  AgendaSlotType,
  useAgendaControllerGetQuery,
  useAgendaControllerUpdateMutation,
} from "~/ui-rtk/api/agendaApi"
import useDate from "~/ui-rtk/hooks/date"
import {
  SLOT_STATUS_META,
  SLOT_TYPE_META,
} from "~/ui-rtk/pages/BrandAgenda/constants"
import { slotProgress } from "~/ui-rtk/pages/BrandAgenda/utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * Query param carrying an agenda slot id into a linked flow. Set by the
 * Brand Agenda slot actions (useAgendaActions).
 */
export const AGENDA_SLOT_PARAM = "agendaSlot"

const QUANTITY_UNIT: Record<AgendaSlotType, string> = {
  AD_BLAST: "ads to blast",
  AD_CREATIVE: "creatives to produce",
  BUDGET_RECOMMENDATION: "updates",
  GENERIC: "items",
}

/**
 * Shows the agenda slot the user arrived from (via `?agendaSlot=<id>`) with
 * its target quantity and live progress. "Start" (→ IN_PROGRESS) is the only
 * manual action — completion is deliberately NOT offered here: the slot is
 * completed by the work itself (links created by blasts / creative builds
 * advance it via the job completion hooks).
 */
export default function AgendaContextBanner({
  className,
}: {
  className?: string
}) {
  const dayjs = useDate()
  const [searchParams, setSearchParams] = useSearchParams()
  const slotId = searchParams.get(AGENDA_SLOT_PARAM)

  const { data: slot } = useAgendaControllerGetQuery(
    { id: slotId ?? "" },
    { skip: !slotId },
  )
  const [updateSlot, { isLoading: isUpdating }] =
    useAgendaControllerUpdateMutation()

  if (!slotId || !slot) return null

  const dismiss = () => {
    searchParams.delete(AGENDA_SLOT_PARAM)
    searchParams.delete("count")
    setSearchParams(searchParams, { replace: true })
  }

  const start = async () => {
    try {
      await updateSlot({
        id: slot.id,
        updateAgendaSlotDto: { status: "IN_PROGRESS" },
      }).unwrap()
      toast.success("Agenda slot started")
    } catch {
      toast.error("Failed to update agenda slot")
    }
  }

  const typeMeta = SLOT_TYPE_META[slot.type]
  const statusMeta = SLOT_STATUS_META[slot.status]
  const progress = slotProgress(slot)

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-lg border border-primary bg-basic-dark-blue px-4 py-3",
        className,
      )}
    >
      <CalendarDaysIcon className="h-5 w-5 shrink-0 text-primary" />

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-white">
          {slot.title}
        </span>
        <span className="text-xs text-basic-neutral">
          <span className={cn("badge badge-xs mr-1", typeMeta.badgeClass)}>
            {typeMeta.label}
          </span>
          {progress
            ? `${progress.completed}/${progress.target} ${QUANTITY_UNIT[slot.type]} · `
            : ""}
          scheduled {dayjs(slot.scheduledDate).format("MMM D")} ·{" "}
          <span className={cn("badge badge-xs", statusMeta.badgeClass)}>
            {statusMeta.label}
          </span>
        </span>
      </div>

      <div className="flex items-center gap-2">
        {(slot.status === "TODO" || slot.status === "BLOCKED") && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={isUpdating}
            onClick={start}
          >
            Start
          </button>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-square"
          onClick={dismiss}
          aria-label="Dismiss agenda context"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
